/**
 * MessageThread — Full conversation view for a single entity thread.
 *
 * Shows entity context header, chronological message list, and composer.
 * Used both in the /messages page and as an inline component in show pages.
 */

import React, { useState, useEffect, useRef } from "react";
import { Typography, Button, Spin, Empty, Tag, theme } from "antd";
import { SendOutlined, LinkOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useComments, useCreateComment } from "@/hooks/useComments";
import { useEntityName, getEntityInfo } from "@/hooks/useEntityName";
import { markThreadSeen } from "@/hooks/useMessageInbox";
import { MessageBubble } from "./MessageBubble";
import { MentionTextarea } from "./MentionTextarea";
import { resolveCommentAuthor, stripContactPrefix } from "@/utils/commentAuthor";

const { Text } = Typography;

interface MessageThreadProps {
  /** WSID of the entity this thread is about */
  entityId: string;
  /** Compact mode for inline use (no header, smaller) */
  compact?: boolean;
  /** Max height for scrollable area */
  maxHeight?: number | string;
}

export const MessageThread: React.FC<MessageThreadProps> = ({
  entityId,
  compact = false,
  maxHeight,
}) => {
  const {
    token: { colorBgContainer, colorBorder, borderRadiusLG },
  } = theme.useToken();

  const navigate = useNavigate();
  const { name: entityName, loading: nameLoading } = useEntityName(entityId);
  const entityInfo = getEntityInfo(entityId);
  const { comments, isLoading, refetch } = useComments({
    relatedTo: entityId,
    refetchInterval: 10000,
  });
  const { createComment, isCreating } = useCreateComment();

  const [newMessage, setNewMessage] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Mark thread as seen when viewing
  useEffect(() => {
    if (entityId && comments.length > 0) {
      markThreadSeen(entityId);
    }
  }, [entityId, comments.length]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [comments.length]);

  const handleSend = async () => {
    if (!newMessage.trim()) return;
    try {
      await createComment({ content: newMessage.trim(), relatedTo: entityId });
      setNewMessage("");
      refetch();
    } catch {
      // Error handled by Refine notification provider
    }
  };

  // Reverse to show chronological (oldest first)
  const chronological = [...comments].reverse();

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: maxHeight || "100%",
        background: colorBgContainer,
        borderRadius: compact ? 0 : borderRadiusLG,
      }}
    >
      {/* Entity context header */}
      {!compact && entityInfo && (
        <div
          style={{
            padding: "12px 16px",
            borderBottom: `1px solid ${colorBorder}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Tag color="blue" style={{ margin: 0 }}>
              {entityInfo.label}
            </Tag>
            <Text strong style={{ fontSize: 14 }}>
              {nameLoading ? "..." : entityName || entityId}
            </Text>
          </div>
          <Button
            type="text"
            size="small"
            icon={<LinkOutlined />}
            onClick={() => navigate(entityInfo.route)}
          >
            Apri
          </Button>
        </div>
      )}

      {/* Messages area */}
      <div
        style={{
          flex: 1,
          overflow: "auto",
          padding: compact ? "8px 12px" : "12px 16px",
        }}
      >
        {isLoading ? (
          <div style={{ textAlign: "center", padding: 40 }}>
            <Spin />
          </div>
        ) : chronological.length === 0 ? (
          <Empty
            description="Nessun messaggio"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            style={{ margin: "40px 0" }}
          />
        ) : (
          <>
            {chronological.map((c: any) => (
              <MessageBubble
                key={c.id}
                content={stripContactPrefix(c.commentcontent || c.description || "")}
                authorName={resolveCommentAuthor(c).name}
                createdTime={c.createdtime || ""}
              />
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Composer */}
      <div
        style={{
          padding: compact ? "8px 12px" : "12px 16px",
          borderTop: `1px solid ${colorBorder}`,
          display: "flex",
          gap: 8,
          alignItems: "flex-end",
        }}
      >
        <div style={{ flex: 1 }}>
          <MentionTextarea
            value={newMessage}
            onChange={setNewMessage}
            rows={compact ? 2 : 3}
            onSubmit={handleSend}
            disabled={isCreating}
          />
        </div>
        <Button
          type="primary"
          icon={<SendOutlined />}
          onClick={handleSend}
          loading={isCreating}
          disabled={!newMessage.trim()}
          style={{ marginBottom: 1 }}
        />
      </div>
    </div>
  );
};
